import React, { useState, useEffect } from "react";
import { TbMail } from "react-icons/tb";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import apiClient from "../../../axios";

const formSchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  email: z.string().email({ message: "Invalid email address" }),
  phone: z.string().optional(),
  message: z.string().min(1, { message: "Message is required" }),
  website: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;
type ButtonState = "idle" | "submitting" | "success" | "error" | "disabled";

const ContactForm: React.FC = () => {
  const { t } = useTranslation("global");
  const [buttonState, setButtonState] = useState<ButtonState>("idle");
  const [disabled, setDisabled] = useState(false);
  
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    if (buttonState === "success" || buttonState === "error") {
      const timer = setTimeout(() => {
        setButtonState("idle");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [buttonState]);

  const onSubmit = async (data: FormData) => {
    setButtonState("submitting");
    try {
      await apiClient.post("/auth/contact", data);
      setButtonState("success");

      setTimeout(() => {
        setButtonState("disabled");
        setDisabled(true);
      }, 3000);

      reset();
    } catch (error) {
      setButtonState("error");
      setDisabled(false);
    }
  };

  const getInputClass = (fieldName: keyof FormData) => `
    w-full p-3 rounded-lg border-2 transition-colors
    ${
      errors[fieldName]
        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
        : "border-blue-200 focus:border-blue-500 focus:ring-blue-500"
    }
  `;

  const buttonClass = {
    idle: "bg-blue-600 hover:bg-blue-700",
    submitting: "bg-blue-400 cursor-not-allowed",
    success: "bg-green-500",
    error: "bg-red-500",
    disabled: "bg-gray-400 cursor-not-allowed"
  };

  const buttonText = {
    idle: t("contactForm.send"),
    submitting: t("contactForm.sending"),
    success: t("contactForm.sent"),
    error: t("contactForm.error"),
    disabled: t("footer.send")
  };

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="md:flex">
        {/* Contact Info */}
        <div className="md:w-2/5 bg-gradient-to-br from-blue-700 to-blue-900 text-white p-8 flex flex-col justify-center">
          <h3 className="text-2xl font-heading font-bold mb-6">
            {t("contact.ways")}
          </h3>
          <p className="text-blue-100 mb-8">
            {t("contact.textShort")}
          </p>
          
          <div className="bg-white/10 p-6 rounded-lg backdrop-blur-sm">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-blue-500 p-2 rounded-full">
                <TbMail className="text-white text-xl" />
              </div>
              <span className="font-medium">contact@isoeval.com</span>
            </div>
            
            <div className="mt-6 pt-6 border-t border-white/20">
              <p className="text-sm text-blue-100">
                {t("contact.responseTime", "Nous vous répondrons dans les plus brefs délais")}
              </p>
            </div>
          </div>
        </div>
        
        {/* Form */}
        <div className="md:w-3/5 p-8">
          <h3 className="text-2xl font-heading font-bold text-gray-900 mb-6">
            {t("contactForm.title", "Envoyez-nous un message")}
          </h3>
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Controller
              name="website"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  className="hidden"
                  aria-hidden="true"
                />
              )}
            />

            <div>
              <label className="block text-sm font-heading font-bold text-gray-700 uppercase mb-2">
                {t("contactForm.nom")}
              </label>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    placeholder={t("contactForm.nomInput")}
                    className={getInputClass("name")}
                  />
                )}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-heading font-bold text-gray-700 uppercase mb-2">
                E-mail *
              </label>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="email"
                    placeholder={t("contactForm.mailInput")}
                    className={getInputClass("email")}
                  />
                )}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
              )}
              <p className="text-sm text-gray-500 mt-1">
                {t("contactForm.checkMail")}
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-heading font-bold text-gray-700 uppercase mb-2">
                {t("contactForm.phone")}
              </label>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    placeholder={t("contactForm.telephoneInput")}
                    className={getInputClass("phone")}
                  />
                )}
              />
              <p className="text-sm text-gray-500 mt-1">
                {t("contactForm.checkTelephone")}
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-heading font-bold text-gray-700 uppercase mb-2">
                {t("contactForm.message")}
              </label>
              <Controller
                name="message"
                control={control}
                render={({ field }) => (
                  <textarea
                    {...field}
                    placeholder={t("contactForm.messageInput")}
                    className={`${getInputClass("message")} h-32 resize-none`}
                  ></textarea>
                )}
              />
              {errors.message && (
                <p className="text-red-500 text-sm mt-1">{errors.message.message}</p>
              )}
            </div>
            
            <button
              type="submit"
              disabled={buttonState === "submitting" || disabled}
              className={`w-full py-3 px-6 rounded-full font-heading font-bold text-white transition-all duration-300 ${buttonClass[buttonState]}`}
            >
              {buttonText[buttonState]}
            </button>
            
            {buttonState === "success" && (
              <div className="p-4 bg-green-100 text-green-800 rounded-lg mt-4">
                {t("contactForm.successMessage", "Votre message a été envoyé avec succès. Nous vous répondrons bientôt!")}
              </div>
            )}
            
            {buttonState === "error" && (
              <div className="p-4 bg-red-100 text-red-800 rounded-lg mt-4">
                {t("contactForm.errorMessage", "Une erreur s'est produite. Veuillez réessayer plus tard.")}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
